<template>
    <div class="item" :class="{ 'has-discount': item.hasDiscount }">
        <div class="head">
            <div class="quantity">{{ model.quantity }}x</div>
            <div class="name">{{ translate(model.name) }}</div>
            <div class="increaser" :class="{ hidden: !canChangeQuantity }">
                <DecreaseBtn :disabled="!isDecreaseAvailable" @click="onDecreaseClick" />
                <IncreaseBtn :disabled="!isIncreaseAvailable" @click="onIncreaseClick" />
            </div>
            <CartItemPrice class="price" :item="item" @click="onPriceClick" />
            <RemoveItemBtn :disabled="!isRemoveAvailable" @click="onRemoveItemClick" />
        </div>

        <div class="subs" v-if="item.options.length">
            <CartItemOptionGroup
                v-for="optionGroup in item.options"
                :key="optionGroup.id"
                :optionGroup="optionGroup"
                :multiplier="item.quantity"
            />
        </div>

        <CartItemCommentEditor
            :item="item"
            :disabled="props.disabled"
            :isOperatorUser="identity.hasOperatorAccess"
            :areItemCommentsAllowed="cart.areItemCommentsAllowed"
        />
    </div>
</template>

<script setup lang="ts">
    import type { ICartItem } from "@/data/model/Customer";
    import { useCartStore } from "@/data/stores/cart";
    import { useIdentityStore } from "@/data/stores/identity";
    import { translate } from "@/shared/localization/language";
    import { computed } from "vue";

    import CartItemOptionGroup from "@/app/components/cart/CartItemOptionGroup.vue";
    import DecreaseBtn from "@/app/components/cart/DecreaseBtn.vue";
    import IncreaseBtn from "@/app/components/cart/IncreaseBtn.vue";
    import RemoveItemBtn from "@/app/components/cart/RemoveItemBtn.vue";
    import CartItemCommentEditor from "@/components/cart/CartItemCommentEditor.vue";
    import CartItemPrice from "@/components/cart/CartItemPrice.vue";

    const cart = useCartStore();
    const identity = useIdentityStore();

    const props = defineProps<{
        item: ICartItem;
        disabled?: boolean;
    }>();

    const emit = defineEmits<{
        decrease: [cartItem: ICartItem];
        increase: [cartItem: ICartItem];
        remove: [cartItem: ICartItem];
    }>();

    const model = computed(() => props.item);

    const cartItemMinQuantity = computed(() => model.value.minQuantity ?? 1);
    const cartItemMaxQuantity = computed(() => model.value.maxQuantity ?? Number.MAX_VALUE);

    const isDecreaseAvailable = computed(() => model.value.quantity > cartItemMinQuantity.value);
    const isIncreaseAvailable = computed(() => model.value.quantity < cartItemMaxQuantity.value);
    const isRemoveAvailable = computed(() => true);

    const canChangeQuantity = computed(() => cartItemMinQuantity.value !== cartItemMaxQuantity.value);

    async function onDecreaseClick() {
        if (!isDecreaseAvailable.value) return;
        emit("decrease", model.value);
    }

    async function onIncreaseClick() {
        if (!isIncreaseAvailable.value) return;
        emit("increase", model.value);
    }

    async function onRemoveItemClick() {
        if (!isRemoveAvailable.value) return;
        emit("remove", model.value);
    }

    async function onPriceClick() {
        if (!model.value.hasDiscount) return;

        cart.showConfirmItemDiscountsRemovalPopup(model.value);
    }
</script>
